import ThankYouWebinarReg from "./ThankYouWebinarReg";
import InlineLoginPrompter from "./InlineLoginPrompter";
import SetPassword from "./SetPassword";
import { useStateWithClientStorage, registerUserOnZephr, startPasswordlessAuthOnZephr, removeFormForSubmit, setFormVisibility, checkZephrUserExists } from "./../utils";
import { WEBINAR_REG_MESSAGES, WEBINAR_SIGN_UP_SID } from "./../consts";

const { useState, useEffect, useCallback, useRef } = window.React;

// Webinar.net webinar ID
let idFromSubURL = null;
if( "string" === typeof adw?.webinar_meta?.registration_url && adw.webinar_meta.registration_url.length > 0 ){
    let parts = adw.webinar_meta.registration_url.split( '/' );
    // Remove empty values
    parts = parts.filter( part => part.length > 0 );
    idFromSubURL = parts[ parts.length - 1 ]; 
}

const localStoragePrefix = 'webinar_user_';
const webcasts_events_key = idFromSubURL !== null ? idFromSubURL : ( String(adw?.webinar_meta?.event_id) || 'undefined' );
const passwordPendingKey = `${localStoragePrefix}passwordPending_${webcasts_events_key}`;

const isDebugMode = new URLSearchParams(window.location.search).has('debug');

function WebinarApp() {

    const [registeredEmail, setRegisteredEmail] = useStateWithClientStorage(`${localStoragePrefix}registeredEmail_${webcasts_events_key}`, null);
    const [regFormIsVisible, setRegFormIsVisible] = useState(false);
    const [submittingForm, setSubmittingForm] = useState(false);
    const [showPasswordCreation, setShowPasswordCreation] = useState(false);
    const [hasZephrUser, setHasZephrUser] = useStateWithClientStorage(`${localStoragePrefix}hasZephrUser_${webcasts_events_key}`, false);
    const [signUpDismissed, setSignUpDismissed] = useStateWithClientStorage(`${localStoragePrefix}signUpDismissed_${webcasts_events_key}`, false);

    const [formFields, setFormFields] = useStateWithClientStorage(`${localStoragePrefix}formFields_${webcasts_events_key}`, null);
    const [userPassword, setUserPassword] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(() => !!Object.entries(window.awGetUserData?.() || {}).length);
    const skipNextUserExistenceCheck = useRef(false);

    const [errorMessage, setErrorMessage] = useState(null);
    const [isBusy, setIsBusy] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [thankYouMessage, setThankYouMessage] = useState(WEBINAR_REG_MESSAGES.success);

    const [passwordIsStrong, setPasswordIsStrong] = useState(false);

    useEffect(() => {
        window.adw.webinar_app = {
            setRegisteredEmail: ({ email, message = null, skipUserExistenceCheck = false }) => {
                skipNextUserExistenceCheck.current = skipUserExistenceCheck;
                setRegisteredEmail(email);
                if (message) {
                    setThankYouMessage(message);
                }
            },
            setRegFormIsVisible: setRegFormIsVisible,
            setSubmittingForm: setSubmittingForm,
            setShowPasswordCreation: setShowPasswordCreation,
            setHasZephrUser: setHasZephrUser,
            setFormFields: setFormFields,
            setIsLoggedIn: setIsLoggedIn,
        };

        window.dispatchEvent(new Event("adw.webinar_app.ready"));
    }, []);

    useEffect(() => {
        localStorage.removeItem(passwordPendingKey);

        if (skipNextUserExistenceCheck.current) {
            skipNextUserExistenceCheck.current = false;
            return;
        }

        if (!registeredEmail || signUpDismissed || hasZephrUser || isLoggedIn) {
            return;
        }

        let ignore = false;
        checkZephrUserExists(registeredEmail)
            .then((exists) => {
                if (ignore) {
                    return;
                }
                if (exists) {
                    setHasZephrUser(true);
                } else {
                    setShowPasswordCreation(true);
                }
            })
            .catch(() => {
                if (!ignore) {
                    setShowPasswordCreation(true);
                }
            });

        return () => {
            ignore = true;
        };
    }, [registeredEmail, signUpDismissed, hasZephrUser, isLoggedIn, setHasZephrUser]);

    useEffect(() => {
        setFormVisibility(regFormIsVisible);
    }, [regFormIsVisible]);

    useEffect(() => {

        // Remove Digioh form wrapper
        if (registeredEmail) {
            removeFormForSubmit();
        }

        const elFloatingBtnWrapper = document.querySelector('.webinar .fixed-button-wrapper');
        if (elFloatingBtnWrapper) {
            elFloatingBtnWrapper.classList.remove('d-flex');
            elFloatingBtnWrapper.classList.add('d-none');
        }

    }, [registeredEmail]);

    const isRequestInFlight = useRef(false);

    const handleZephrAccCreation = useCallback(async () => {
        if (isRequestInFlight.current) {
            return;
        }
        isRequestInFlight.current = true;
        setErrorMessage(null);
        setIsBusy(true);
        try {

            const copyFormFields = { ...formFields };
            if (!('sid' in copyFormFields)) {
                copyFormFields['sid'] = WEBINAR_SIGN_UP_SID;
            }
            const messageAndTrackingIdObject = await registerUserOnZephr({ fieldValues: copyFormFields, email: registeredEmail, password: userPassword });

            if (messageAndTrackingIdObject?.status === 409) {
                setErrorMessage('This email already has an ADWEEK account.');
                setIsSuccess(false);
                setHasZephrUser(true);
                return;
            }
            if (!messageAndTrackingIdObject?.ok) {
                setErrorMessage(messageAndTrackingIdObject?.message || 'Unable to create your ADWEEK account.');
                setIsSuccess(false);
                return;
            }

            setHasZephrUser(true);
            try {
                await startPasswordlessAuthOnZephr({ email: registeredEmail, action: 'register' });
                setErrorMessage(null);
                setIsSuccess(true);
            } catch (err) {
                console.error("Token exchange failed after registration", err);
                setErrorMessage('Your account was created, but we could not send the verification email. Use "Sign in" to request a login link.');
            }
        } catch (err) {
            console.error("Error in Zephr Registration", err);
            setErrorMessage('Unable to create your ADWEEK account.');
        } finally {
            setIsBusy(false);
            isRequestInFlight.current = false;
        }
    }, [registeredEmail, formFields, userPassword, setHasZephrUser]);

    const dismissSignUp = useCallback(() => {
        setShowPasswordCreation(false);
        setSignUpDismissed(true);
    }, [setSignUpDismissed]);

    return (
        <div>
            {
                isDebugMode && (
                    <div className="alert alert-info">
                        <p><strong>Debug Mode Enabled</strong></p>
                        {/* Key for registeredEmail in client storage */}
                        <p>localStorage Key for registeredEmail: {`${localStoragePrefix}registeredEmail_${webcasts_events_key}`}</p>
                        <p>registeredEmail: {registeredEmail}</p>
                        <p>regFormIsVisible: {regFormIsVisible.toString()}</p>
                        <p>submittingForm: {submittingForm.toString()}</p>
                        <p>showPasswordCreation: {showPasswordCreation.toString()}</p>
                        <p>hasZephrUser: {hasZephrUser.toString()}</p>
                        <p>signUpDismissed: {signUpDismissed.toString()}</p>
                        <p>formFields: {JSON.stringify(formFields)}</p>
                        <p>isLoggedIn: {isLoggedIn?.toString()}</p>
                    </div>
                )
            }
            {
                !isLoggedIn &&
                (
                    !(showPasswordCreation || registeredEmail)
                    && <InlineLoginPrompter />
                )
            }
            {(!registeredEmail?.length && submittingForm) && <Loader />}

            {registeredEmail && (
                <ThankYouWebinarReg email={registeredEmail} thankYouMessage={thankYouMessage} />
            )}

            {showPasswordCreation && registeredEmail &&
                <SetPassword
                    userPassword={userPassword}
                    setUserPassword={setUserPassword}
                    handleZephrAccCreation={handleZephrAccCreation}
                    dismissSignUp={dismissSignUp}
                    registeredEmail={registeredEmail}
                    errorMessage={errorMessage}
                    isBusy={isBusy}
                    isSuccess={isSuccess}
                    hasZephrUser={hasZephrUser}
                    passwordIsStrong={passwordIsStrong}
                    setPasswordIsStrong={setPasswordIsStrong}
                />
            }


        </div>
    );
}

function Loader() {
    return (
        <div className="row justify-content-center my-2">
            <div className="col-12 text-center">
                <div className="spinner-border mx-auto" role="status">
                    <span className="sr-only"></span>
                </div>
            </div>
        </div>
    );
}

export default WebinarApp;
